import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { subscriptionViewModel } from '../../view-model/professional/subscription-view-model';
import subscriptionStore from '../../store/subscription-store';
import SubscriptionPlanCard from '../../components/subscription/subscription-plan-card';
import { PlanName } from '../../model/professional/subscription-model';
import { Loader2 } from 'lucide-react';

const Subscription: React.FC = observer(() => {
  const { t } = useTranslation();
  const [showChangePlanConfirm, setShowChangePlanConfirm] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<PlanName | null>(null);
  const [changePlanMessage, setChangePlanMessage] = useState('');

  useEffect(() => {
    // Initialiser les données
    subscriptionViewModel.initialize();
  }, []);

  const handleSelectPlan = async (planId: PlanName) => {
    // Si l'utilisateur a déjà un abonnement, c'est un changement de plan
    if (subscriptionStore.currentSubscription) {
      const isUpgrade = subscriptionViewModel.isUpgrade(planId);
      const isDowngrade = subscriptionViewModel.isDowngrade(planId);

      let confirmMessage = t('subscription.confirmChange');
      if (isUpgrade) {
        confirmMessage = t('subscription.confirmUpgrade');
      } else if (isDowngrade) {
        confirmMessage = t('subscription.confirmDowngrade');
      }

      setSelectedPlanId(planId);
      setChangePlanMessage(confirmMessage);
      setShowChangePlanConfirm(true);
    } else {
      // Nouvel abonnement, créer une session de checkout
      await subscriptionViewModel.subscribe(planId);
    }
  };

  const handleConfirmChangePlan = async () => {
    if (selectedPlanId) {
      setShowChangePlanConfirm(false);
      await subscriptionViewModel.changePlan(selectedPlanId);
      setSelectedPlanId(null);
      setChangePlanMessage('');
    }
  };

  // Afficher un loader pendant le chargement initial
  if (subscriptionStore.isLoading && subscriptionStore.availablePlans.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary dark:text-primary-light mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">{t('subscription.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col space-y-6 overflow-auto pb-6">
      {/* Section des plans disponibles */}
      <div>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {subscriptionStore.currentSubscription
              ? t('subscription.changePlanTitle')
              : t('subscription.choosePlanTitle')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {subscriptionStore.currentSubscription
              ? t('subscription.changePlanDescription')
              : t('subscription.choosePlanDescription')}
          </p>
        </div>

        {/* Grille des plans */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {subscriptionStore.availablePlans.map((plan) => {
            const isCurrentPlan = subscriptionStore.currentSubscription?.planName === plan.id;
            const isPopular = plan.id === 'premium';

            return (
              <SubscriptionPlanCard
                key={plan.id}
                plan={plan}
                isCurrentPlan={isCurrentPlan}
                isPopular={isPopular}
                onSelect={handleSelectPlan}
                disabled={subscriptionStore.isLoading}
              />
            );
          })}
        </div>

        {/* Message de contact */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('subscription.contactMessage', 'Des questions sur nos plans ? ')}
            <button
              onClick={() => {
                if (window.$crisp) {
                  window.$crisp.push(['do', 'chat:open']);
                }
              }}
              className="text-primary dark:text-primary-light hover:underline font-medium"
            >
              {t('subscription.contactUs', 'Contactez-nous')}
            </button>
          </p>
        </div>
      </div>

      {/* Modal de confirmation de changement de plan */}
      {showChangePlanConfirm && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
            onClick={() => setShowChangePlanConfirm(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                {t('subscription.changePlanConfirmTitle')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {changePlanMessage}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowChangePlanConfirm(false)}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {t('subscription.changePlanConfirmNo')}
                </button>
                <button
                  onClick={handleConfirmChangePlan}
                  className="flex-1 px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary-dark transition-colors"
                >
                  {t('subscription.changePlanConfirmYes')}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  );
});

export default Subscription;
